import clsx from "clsx";
import { Modal } from "flowbite-react";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import FileUpload from "@/app/(customers)/ask/[conversationId]/FileUpload";
import Button from "@/components/library/Button";
import { BRANDS } from "@/enums/brands";
import { ChatDetails, GptDetails } from "@/types/chat";
import { FileUploadTypes } from "@/utils/files";
import Icon from "../library/Icon";
import ChatFileUpload from "./ChatFileUpload";

type AskUserInputProps = {
  handleAddMessage: (message: string) => Promise<void>;
  disabled: boolean;
  message: string;
  setMessage: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  fileType: FileUploadTypes;
  details?: ChatDetails | GptDetails;
  conversationId?: string;
  handleFileAction?: (action: "delete" | "add", url: string) => Promise<void>;
  isWingman?: boolean;
  placeholder?: string;
  showInitialState?: boolean;
};

const AskUserInput: React.FC<AskUserInputProps> = ({
  handleAddMessage,
  disabled,
  message,
  setMessage,
  fileType,
  details,
  conversationId,
  handleFileAction,
  isWingman,
  placeholder,
  showInitialState,
}) => {
  const { t } = useTranslation("ask");
  const { t: ct } = useTranslation("common");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showUploadModal, setShowUploadModal] = React.useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const brand = details?.brand;

  // Initialize and update textarea height based on content, and focus when visible
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.overflowY = "hidden";

      if (!disabled && isVisible) {
        inputRef.current.focus();
      }
    }
  }, [disabled, isVisible, showInitialState]);

  // Set up IntersectionObserver to detect when the component is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  // Auto-resize function for the textarea
  const autoResizeTextarea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e);

    // Auto-grow textarea height based on content
    if (inputRef.current) {
      // Reset height to calculate scroll height correctly
      inputRef.current.style.height = "auto";

      // Calculate new height based on content
      const scrollHeight = inputRef.current.scrollHeight;
      const lineHeight = 24; // Assuming 24px line height
      const maxHeight = lineHeight * 4; // 4 lines maximum

      if (scrollHeight <= maxHeight) {
        // If content fits within limit, grow automatically
        inputRef.current.style.height = `${Math.max(42, scrollHeight)}px`;
        inputRef.current.style.overflowY = "hidden";
      } else {
        // If content exceeds limit, set to max height and enable scrolling
        inputRef.current.style.height = `${maxHeight}px`;
        inputRef.current.style.overflowY = "auto";
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={clsx(
        "rounded-lg",
        "relative",
        { "bg-panel shadow-xl p-4": !isWingman },
        { "!my-2": isWingman && !showInitialState },
        {
          "!m-0 !p-0 !bg-transparent !shadow-none":
            showInitialState && message.length === 0,
        }
      )}
    >
      <div className={clsx("relative", { "mx-2": isWingman })}>
        <textarea
          ref={inputRef}
          placeholder={
            placeholder ||
            t(
              fileType === FileUploadTypes.CHAT
                ? "askChatPlaceholder"
                : "askGailPlaceholder"
            )
          }
          className={clsx(
            "w-full",
            "p-2",
            "pr-16",
            "border",
            "dark:bg-gray_comp",
            "!border-gray-300",
            "dark:!border-gray-600",
            "dark:!ring-gray-600",
            "rounded-lg",
            "focus:outline-none",
            "focus:ring-2",
            "focus:ring-gailPrimary",
            "text-sm",
            "resize-none",
            "min-h-[42px]",
            {
              "bg-gray-200": disabled,
              "text-gray-500":
                (disabled && !(showInitialState && message.length === 0)) ||
                (showInitialState && message.length === 0),
              "!bg-transparent": isWingman,
              "!ring-gray-300": !isWingman,
              "text-2xl": showInitialState && message.length === 0,
              "font-semibold": showInitialState && message.length === 0,
              "!border-0": showInitialState,
              "!ring-0": showInitialState,
              "focus:!ring-0": showInitialState,
              "focus:!outline-none": showInitialState,
            }
          )}
          value={message}
          onChange={autoResizeTextarea}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && message.trim().length > 0) {
              e.preventDefault();
              handleAddMessage(message);
              setMessage({
                target: { value: "" },
              } as React.ChangeEvent<HTMLTextAreaElement>);

              // Reset textarea height
              if (inputRef.current) {
                inputRef.current.style.height = "42px";
                inputRef.current.style.overflowY = "hidden";
              }
            }
          }}
          rows={1}
        />
        <div className="absolute top-1 right-2">
          <div className="flex flex-row gap-1">
            <Button
              variant={isWingman ? "gail2" : "gail"}
              type="primary"
              disabled={disabled || message.length === 0}
              onClick={() => {
                handleAddMessage(message);
                setMessage({
                  target: { value: "" },
                } as React.ChangeEvent<HTMLTextAreaElement>);

                // Reset textarea height
                if (inputRef.current) {
                  inputRef.current.style.height = "42px";
                  inputRef.current.style.overflowY = "hidden";
                }
              }}
              className={clsx(
                "h-8 w-8 !p-0 rounded-md chat-input-button flex items-center justify-center hover:bg-gail-primary/80 focus:outline-none disabled:opacity-50",
                {
                  hidden: showInitialState && message.length === 0,
                }
              )}
            >
              {brand === BRANDS.GAIL ? (
                <Icon icon={"arrow-up"} />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="m-auto"
                >
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              )}
            </Button>
            <Button
              variant={isWingman ? "gail2" : "gail"}
              type="secondary"
              disabled={disabled}
              className={clsx(
                "h-8 w-8 !p-0 rounded-md chat-input-button flex items-center justify-center focus:outline-none",
                {
                  hidden:
                    (isWingman && disabled) ||
                    (isWingman && details?.maxFiles === 0) ||
                    (isWingman && (details?.fileTypes?.length || 0) === 0),
                }
              )}
              onClick={() => setShowUploadModal(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="m-auto"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </Button>
          </div>
        </div>
      </div>

      <Modal show={showUploadModal} onClose={() => setShowUploadModal(false)}>
        <Modal.Header>
          {t("uploadFileTitle")}
          <div className={clsx("text-sm", "text-600")}>
            {t("uploadFileDescription")}
          </div>
        </Modal.Header>
        <Modal.Body>
          {fileType !== FileUploadTypes.CHAT && <FileUpload />}
          {fileType === FileUploadTypes.CHAT && details && (
            <ChatFileUpload
              fileType={fileType}
              details={details}
              conversationId={conversationId || ""}
              handleFileAction={handleFileAction}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="w-full">
            <Button
              variant={isWingman ? "gail2" : "gail"}
              type="primary"
              onClick={() => setShowUploadModal(false)}
              className="float-right"
            >
              {ct("close")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AskUserInput;
